import type { CSSProperties, ReactNode } from 'react';
import * as RadixDialog from '@radix-ui/react-dialog';

// A Radix-backed shell for the billing dialogs. Radix provides focus trapping,
// focus return on close, Escape-to-close, scroll lock, and `role="dialog"` +
// `aria-modal` for free — the things the hand-rolled overlays were missing. The
// visible content (headings, forms, buttons) is passed as children unchanged, so
// this adds accessibility without altering the visual design.

const SR_ONLY: CSSProperties = {
  position: 'absolute',
  width: 1,
  height: 1,
  padding: 0,
  margin: -1,
  overflow: 'hidden',
  clip: 'rect(0,0,0,0)',
  whiteSpace: 'nowrap',
  border: 0,
};

interface AccessibleModalProps {
  /** Accessible name announced when the dialog opens (screen-reader only). */
  title: string;
  /** Accessible description (screen-reader only). Defaults to the title. */
  description?: string;
  /** Max width of the dialog card. */
  maxWidth?: number;
  /** When false, Escape and outside-click do not close (e.g. while a payment is processing). */
  dismissible?: boolean;
  /**
   * When false, the dialog does NOT auto-focus its first focusable element on open —
   * avoids an unprompted focus ring on a single-CTA dialog. Focus trapping on Tab and
   * Escape-to-close still work. Defaults to true (standard dialog behaviour).
   */
  autoFocus?: boolean;
  onClose: () => void;
  children: ReactNode;
}

export function AccessibleModal({
  title,
  description,
  maxWidth = 440,
  dismissible = true,
  autoFocus = true,
  onClose,
  children,
}: AccessibleModalProps) {
  return (
    <RadixDialog.Root
      open
      onOpenChange={(isOpen) => {
        if (!isOpen) {
          onClose();
        }
      }}
    >
      <RadixDialog.Portal>
        <RadixDialog.Overlay
          className="bg-black/60 backdrop-blur-sm sm:backdrop-blur-md"
          style={{ position: 'fixed', inset: 0, zIndex: 10_000 }}
        />
        <RadixDialog.Content
          onOpenAutoFocus={(e) => {
            if (!autoFocus) {
              e.preventDefault();
            }
          }}
          onEscapeKeyDown={(e) => {
            if (!dismissible) {
              e.preventDefault();
            }
          }}
          onInteractOutside={(e) => {
            if (!dismissible) {
              e.preventDefault();
            }
          }}
          className="rounded-2xl border border-subtle bg-surface shadow-[0_40px_100px_-20px_rgba(0,0,0,0.8)] outline-none focus:outline-none focus-visible:outline-none"
          style={{
            position: 'fixed',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%,-50%)',
            zIndex: 10_001,
            width: 'calc(100% - 24px)',
            maxWidth,
            // dvh, not vh: on iOS `100vh` is the tallest viewport, which hid the Pay button under the toolbar.
            maxHeight: 'calc(100dvh - 24px)',
            overflowY: 'auto',
            overscrollBehavior: 'contain',
            padding: 22,
          }}
        >
          <RadixDialog.Title style={SR_ONLY}>{title}</RadixDialog.Title>
          <RadixDialog.Description style={SR_ONLY}>{description ?? title}</RadixDialog.Description>
          {children}
        </RadixDialog.Content>
      </RadixDialog.Portal>
    </RadixDialog.Root>
  );
}
